import React, { useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import { Color } from "../../../../../../../components/Form/Button";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import LoadingModal from "../../../../../../../components/LoadingModal";
import { PRIVATE_ROUTES } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import { Waybill } from "../../../../../../../models/waybill";
import {
  AutomaticSequenceOptions,
  ShipmentTrackingStatus,
  ShipmentType,
} from "../../../../../../../util/enums";
import { useShipmentContext } from "../../hooks/useContext";
import { DefaultProps } from "../../types";
import { AirForm } from "./AirForm";
import SeaForm from "./SeaForm";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";
import { useQuery } from "@tanstack/react-query";
import AutomaticSequencingService from "Services/automatic.sequencing.service";
interface DetailsProps extends DefaultProps {
  isLoading?: boolean;
  isSubscribing?: boolean;
  hasBeenSaved?: boolean;
  waybillView?: Waybill;
  onSave?: () => void;
  setOpenConfirmationDeleteModal?: (value: boolean) => void;
  onSaveAndSubscribe?: () => void;
  onCancelOverride?: () => void;
  publishDisabled?: boolean;
  permission?: {
    delete: boolean;
  };
}

export const WaybillsDetails = (props: DetailsProps) => {
  const {
    isLoading,
    isSubscribing,
    errors = [],
    hasBeenSaved,
    onSave,
    setOpenConfirmationDeleteModal,
    onSaveAndSubscribe,
    onCancelOverride,
    publishDisabled,
    onViewMode = false,
    waybillView,
    permission,
  } = props;

  const { currentUser } = useAppState();

  const {
    shipment,
    setShipment,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    minContainerSequence,
    syncReferenceWithOrder,
  } = useShipmentContext();
  const { t: translate } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (
      shipment &&
      !shipment?.shipper &&
      !shipment?.id &&
      !waybillView &&
      !Object.getOwnPropertyNames(shipment)?.includes("shipper")
    ) {
      setShipment(
        {
          ...shipment,
          shipper: currentUser?.company?.name,
        },
        true
      );
    }
  }, [shipment]);

  const { isFetching: fetchingAutomaticSequenceRef } = useQuery(
    ["automatic.sequence.available", AutomaticSequenceOptions.SHIPMENT],
    () =>
      AutomaticSequencingService.getAvailableSequence(Number(currentUser?.companyId), {
        type: AutomaticSequenceOptions.SHIPMENT,
      }),
    {
      enabled:
        !!currentUser?.companyId &&
        !!AutomaticSequenceOptions.SHIPMENT &&
        !syncReferenceWithOrder,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data) {
          const rawNext = response.data.nextValue;
          const effectiveNext =
            minContainerSequence != null
              ? Math.max(rawNext, minContainerSequence + 1)
              : rawNext;
          const refCode = response.data.prefix + effectiveNext;
          if (!shipment?.containerId) {
            setShipment({ ...shipment, containerId: refCode });
          }
        }
      },
    }
  );

  const textFromShipmentForm = useMemo(() => {
    if (shipment?.id && shipment.statusShipment?.toLowerCase() !== ShipmentTrackingStatus.DRAFT) {
      return translate("update");
    }

    return translate("create");
  }, [shipment?.id, shipment?.statusShipment]);

  const classes = useStyles();

  const renderFormByType = () => {
    switch (shipment?.shipmentType) {
      case ShipmentType.AIR:
        return <AirForm errors={errors} onViewMode={onViewMode} />;
      default:
        return (
          <SeaForm
            onViewMode={onViewMode}
            waybillView={waybillView}
            errors={errors}
            isFetchingReference={fetchingAutomaticSequenceRef}
          />
        );
    }
  };

  return onViewMode ? (
    <Grid container direction="column" spacing={2}>
      {renderFormByType()}
    </Grid>
  ) : (
    <React.Fragment>
      <Grid container direction="column" spacing={2}>
        <div
          style={{
            padding: "32px",
          }}
        >
          {renderFormByType()}
        </div>

        <LoadingModal showing={isLoading || isSubscribing} />
      </Grid>
      <div
        style={{
          margin: "8px 0px 0px 0px",
          color: "red",
          // borderTop: `1px solid ${colors.gray200}`,
        }}
      >
        <div
          className={shipment?.id ? classes.withInactiveButton : classes.withoutInactiveButton}
          style={{
            paddingLeft: "24px",
            paddingRight: "24px",
            margin: "16px 0px",
          }}
        >
          <ActionButtons
            inactiveDisabled={isLoading}
            inactiveIcon={<FiTrash2 size={12} color="#fff" />}
            textInactive={translate("cancelShipment")}
            onInactive={
              shipment?.id && permission?.delete
                ? () => {
                    setHasUnsavedChanges(false);
                    if (setOpenConfirmationDeleteModal) setOpenConfirmationDeleteModal(true);
                  }
                : undefined
            }
            textSavePublish={textFromShipmentForm}
            customColorSave={shipment?.marineTrafficId ? Color.SUCCESS : Color.SUCCESS_OUTLINED}
            customColorCancel={Color.TEXT_INFO}
            showSaveButton={!(shipment?.fetchedData && shipment?.shipmentType === ShipmentType.AIR)}
            textSave={shipment?.marineTrafficId ? translate("update") : translate("saveAsDraft")}
            customColorSavePublish={Color.SECONDARY_DARK}
            dataCySave="save-button"
            dataCySavePublish="save-and-subscribe-button"
            savePublishDisabled={isLoading || isSubscribing || publishDisabled}
            cancelDisabled={isLoading}
            saveDisabled={isLoading || isSubscribing}
            onCancel={() => {
              if (onCancelOverride) {
                onCancelOverride();
                return;
              }
              if (hasBeenSaved && hasUnsavedChanges) {
                setHasUnsavedChanges(false);
              }
              history.push(PRIVATE_ROUTES.SHIPMENTS);
            }}
            onSave={onSave}
            onSavePublish={onSaveAndSubscribe}
          />
        </div>
      </div>
    </React.Fragment>
  );
};
